import { HtmlParser } from '@calendly/html-parser'
import { I18n } from '@calendly/i18n'
import { Button } from '@calendly/ui/components/button'
import { Body, Footer, Header, Popup } from '@calendly/ui/components/popup'
import { useElements, useStripe } from '@stripe/react-stripe-js'
import { debounce } from 'lodash-es'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useGetUserQuery } from 'common'
import StripeContainer from 'components/core/payment/stripe/container'
import { changeQuantity } from 'features/billing/actions/change_quantity'
import { fetchUpcomingInvoice } from 'features/billing/actions/fetch_upcoming_invoice'
import {
  isTaxIdError,
  ModalContentStrategyManager,
  TaxIdWarning,
  UpcomingInvoice,
  UpcomingInvoiceError
} from 'features/billing/modals/shared'
import { ChangeCard } from 'features/billing/modals/shared/change_card'
import { invitationsQuantitySelector } from 'features/billing/modals/subscription_modal/selectors'
import { resetPaymentStatus } from 'features/billing/payment_slice'
import {
  activeSubscriptionCurrencySelector,
  isPaymentMethodCCSelector,
  isSubscriptionExpiringSelector,
  minQuantitySelector,
  paymentAttemptCardErrorSelector,
  paymentAttemptErrorSelector,
  paymentPendingSelector,
  paymentSuccessfulSelector,
  subscriptionProviderSelector,
  subscriptionQuantitySelector,
  currentSubscriptionCardSelector
} from 'features/billing/selectors'
import {
  isUpcomingInvoiceLoadingSelector,
  upcomingInvoiceDataSelector,
  upcomingInvoiceErrorSelector
} from 'features/billing/selectors/upcoming_invoice'
import {
  billingAddressCountrySelector,
  billingAddressSelector,
  secondaryTaxIdSelector,
  taxIdSelector
} from 'features/billing/stages/paid_stage/billing_information_card/selectors'
import { resetInvoice } from 'features/billing/upcoming_invoice_slice'
import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events/constants'
import { useTrack } from 'hooks/analytics_events/useTrack'

import { ModalContentContainer } from './modal_content_container'
import { ErrorMessage, Paragraph, QuantityInput, QuantityInputContainer } from './styles'
import { SuccessPopup } from './success'
import { useVatFlagAndCountryEnabled } from '../../../../hooks/feature_flags/use_vat_flag_and_country_enabled'
import { AddOrEditTaxInformationButton } from '../shared/add_or_edit_tax_information_button'


const validateAndFetch = (onValidateAndFetch) => onValidateAndFetch()
const debounceValidateAndFetch = debounce(validateAndFetch, 800)

export const ChangeQuantityModal = ({
  onClose,
  modalType,
  stripe,
  elements,
  onSubmit,
  onSuccess: onSuccessCallback,
  defaultQuantity,
  onExpand = () => { },
}) => {
  const dispatch = useDispatch()
  const track = useTrack()
  const modalStrategy = ModalContentStrategyManager().getStrategy(modalType)
  const {
    displayChangeCard,
    getExtraQuantity,
    titleI18n,
    reactivateI18n,
    inputActionI18n,
    confirmButtonI18n,
    getValidationSchema
  } = modalStrategy

  const [cardFieldExpanded, setCardFieldExpanded] = useState(false)
  const [cardFieldError, setCardFieldError] = useState()
  const [cardFieldEmpty, setCardFieldEmpty] = useState(true)
  const [paymentElementComplete, setPaymentElementComplete] = useState(false)
  const isInvoiceLoading = useSelector(isUpcomingInvoiceLoadingSelector)
  const invoice = useSelector(upcomingInvoiceDataSelector)
  const currentQuantity = useSelector(subscriptionQuantitySelector)
  const provider = useSelector(subscriptionProviderSelector)
  const minQuantity = useSelector(minQuantitySelector)
  const invitationsQuantity = useSelector(invitationsQuantitySelector)
  const paymentPending = useSelector(paymentPendingSelector)
  const paymentSuccessful = useSelector(paymentSuccessfulSelector)
  const paymentAttemptError = useSelector(paymentAttemptErrorSelector)
  const paymentAttemptCardError = useSelector(paymentAttemptCardErrorSelector)
  const isSubscriptionExpiring = useSelector(isSubscriptionExpiringSelector)
  const upcomingInvoiceError = useSelector(upcomingInvoiceErrorSelector)
  const taxId = useSelector(taxIdSelector)
  const secondaryTaxId = useSelector(secondaryTaxIdSelector)
  const billingAddress = useSelector(billingAddressSelector)
  const billingAddressCountry = useSelector(billingAddressCountrySelector)
  const isInvoiceQuantityError = upcomingInvoiceError && (
    upcomingInvoiceError.isMinimumSeatError || upcomingInvoiceError.isMaximumSeatError
  )
  const isCreditCard = useSelector(isPaymentMethodCCSelector)
  const [error, setError] = useState('')
  const [extraQuantity, setExtraQuantity] = useState(defaultQuantity ?? '')
  // Show payment errors in the main error area, including card errors from API
  // Card field errors (from Stripe input) are handled separately via cardFieldError
  const inputError = paymentAttemptError || error
  const invalidTaxIdError = isTaxIdError(cardFieldError)
  const currency = useSelector(activeSubscriptionCurrencySelector)
  const shouldShowVatFlow = useVatFlagAndCountryEnabled(billingAddressCountry)
  const [currentInvoiceQuantity, setCurrentInvoiceQuantity] = useState('')
  const taxIdRef = useRef(taxId)
  const secondaryTaxIdRef = useRef(secondaryTaxId)
  const isStripeProvider = provider === 'stripe'
  const { data: user } = useGetUserQuery()
  const countryCodeFromUser = user?.country_code
  const { address_zip: billingZip } = useSelector(currentSubscriptionCardSelector)

  const onValidateAndFetch = useCallback((value) => {
    const validationSchema = getValidationSchema(minQuantity, currentQuantity)

    try {
      const quantity = validationSchema.validateSync(value)

      setError('')
      setExtraQuantity(quantity)
      setCardFieldExpanded(false)
      dispatch(resetPaymentStatus())
      dispatch(fetchUpcomingInvoice({ extra_quantity: getExtraQuantity(quantity) }))
    } catch (err) {
      if (err.name === 'ValidationError') setError(err.errors[0])
      else throw err
    }
  }, [
    dispatch,
    minQuantity,
    currentQuantity,
    getValidationSchema,
    getExtraQuantity
  ])

  useEffect(() => {
    const taxIdChanged = taxIdRef.current !== taxId
    const secondaryTaxIdChanged = secondaryTaxIdRef.current !== secondaryTaxId

    if (
      (extraQuantity && (parseInt(currentInvoiceQuantity, 10) !== parseInt(extraQuantity, 10)))
      || taxIdChanged
      || secondaryTaxIdChanged
    ) {
      setCurrentInvoiceQuantity(extraQuantity || '')
      debounceValidateAndFetch(() => { onValidateAndFetch(parseInt(extraQuantity, 10)) })

      taxIdRef.current = taxId
      secondaryTaxIdRef.current = secondaryTaxId
    }
  }, [currentInvoiceQuantity, extraQuantity, onValidateAndFetch, taxId, secondaryTaxId])

  const handleCloseModal = () => {
    dispatch(resetPaymentStatus())
    dispatch(resetInvoice())
    onClose()
  }

  const handleCardFieldChange = (event) => {
    setCardFieldEmpty(event.empty)
    setPaymentElementComplete(event.complete)
    if (event.complete) {
      setCardFieldExpanded(true)
    }
    if (cardFieldError) {
      setCardFieldError(null)
    }
    if (event.error) {
      setCardFieldError(event.error.message)
    }
  }
  const onConfirm = () => {
    const quantity = parseInt(extraQuantity, 10) || 0
    const params = {
      quantity: currentQuantity + getExtraQuantity(quantity)
    }

    if ((cardFieldExpanded && !cardFieldEmpty) || paymentElementComplete) {
      params.stripe = stripe
      params.elements = elements
      params.billingAddress = billingAddress
      params.isStripeProvider = isStripeProvider
      if (isStripeProvider) {
        params.billingCountry = countryCodeFromUser
        params.billingZip = billingZip
      }
      if (shouldShowVatFlow) params.taxId = invalidTaxIdError ? '' : taxId
    }

    if (shouldShowVatFlow) {
      track({
        name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
        properties: {
          action: 'confirming_add_seats',
          location: 'add_seats_quick_checkout_flow',
          modal_name: 'add_seats'
        }
      })
    }

    dispatch(changeQuantity(params))
  }

  const onAddSeats = () => {
    onConfirm()
    if (onSubmit) onSubmit()
  }

  useEffect(() => {
    setCardFieldError(paymentAttemptCardError)
  }, [paymentAttemptError, paymentAttemptCardError])

  useEffect(() => {
    dispatch(resetInvoice())
  }, [dispatch])

  useEffect(() => {
    if (invitationsQuantity) {
      const quantity = invitationsQuantity + minQuantity - currentQuantity
      setExtraQuantity(quantity)
      setError('')
      dispatch(fetchUpcomingInvoice({ extra_quantity: quantity }))
    }
  }, [dispatch, invitationsQuantity, minQuantity, currentQuantity])

  if (paymentSuccessful) {
    const onSuccess = () => {
      dispatch(resetInvoice())
      onClose()
      if (onSuccessCallback) onSuccessCallback()
    }
    const quantity = parseInt(extraQuantity, 10) || 0
    const newQuantity = currentQuantity + getExtraQuantity(quantity)
    const isScheduled = provider === 'stripe' && quantity < 0
    return <SuccessPopup onClose={onSuccess} isScheduled={isScheduled} newQuantity={newQuantity} billingDate={invoice.next_payment_date} />
  }

  if (upcomingInvoiceError && !isInvoiceQuantityError) {
    return (
      <UpcomingInvoiceError onClose={handleCloseModal} strategy={modalStrategy} />
    )
  }

  // i18n-tasks-use t('publisher.billing.change_quantity.remove')
  return (
    <Popup onClose={handleCloseModal}>
      <Header title={titleI18n} subtitle={I18n.t('publisher.billing.prices_are_in_currency', { currency_code: currency })} />
      <Body>
        {reactivateI18n && isSubscriptionExpiring && (
          <Paragraph>
            <strong>{reactivateI18n}</strong>
          </Paragraph>
        )}
        <QuantityInputContainer>
          <span>{inputActionI18n}</span>
          <QuantityInput
            aria-label={I18n.t('publisher.billing.change_quantity.input_label')}
            name="quantity"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            disabled={isInvoiceLoading}
            onChange={(e) => setExtraQuantity(e.target.value)}
            value={extraQuantity}
          />
          <span>{I18n.t('publisher.billing.change_quantity.seats')}</span>
        </QuantityInputContainer>
        {(inputError || isInvoiceQuantityError) && (
          <ErrorMessage data-component="error-message">
            {HtmlParser(inputError || upcomingInvoiceError.errorMessage)}
          </ErrorMessage>
        )}

        <ModalContentContainer
          isLoading={isInvoiceLoading}
          showPrompt={!invoice}
          prompt={I18n.t('publisher.billing.change_quantity.prompt')}
        >
          <UpcomingInvoice strategy={modalStrategy} />
          {displayChangeCard && isCreditCard && (
            <ChangeCard
              expanded={cardFieldExpanded}
              error={invalidTaxIdError ? null : cardFieldError}
              onSubmit={onConfirm}
              onExpand={() => {
                setCardFieldExpanded(true)
                onExpand()
                track({
                  name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
                  properties: {
                    action: 'changing_card',
                    location: 'add_seats_quick_checkout_flow',
                    modal_name: 'add_seats'
                  }
                })
              }}
              onChange={handleCardFieldChange}
              onReady={(e) => e.focus()}
            />
          )}
          {shouldShowVatFlow && (<AddOrEditTaxInformationButton location="add_seats_quick_checkout_flow" />)}
          {(invalidTaxIdError && !shouldShowVatFlow) && <TaxIdWarning />}
        </ModalContentContainer>

      </Body>
      <Footer>
        <Button
          decoration="secondary-outline"
          onClick={() => {
            if (shouldShowVatFlow) {
              track({
                name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
                properties: {
                  action: 'canceling_add_seats',
                  location: 'add_seats_quick_checkout_flow',
                  modal_name: 'add_seats'
                }
              })
            }
            handleCloseModal()
          }}
        >
          {I18n.t('publisher.action.cancel')}
        </Button>
        <Button
          className={`add-seats-${confirmButtonI18n}`}
          loading={paymentPending}
          disabled={
            ((!!inputError || isInvoiceQuantityError) && !((cardFieldExpanded && !cardFieldEmpty) || paymentElementComplete))
            || isInvoiceLoading || !invoice
          }
          onClick={onAddSeats}
        >
          {confirmButtonI18n}
        </Button>
      </Footer>
    </Popup>
  )
}

const UnwrappedIncreaseQuantityModal = (props) => {
  const stripe = useStripe()
  const elements = useElements()

  return (<ChangeQuantityModal {...props} stripe={stripe} elements={elements} modalType="IncreaseQuantity" />)
}

export const IncreaseQuantityModal = (props) => (
  <StripeContainer>
    <UnwrappedIncreaseQuantityModal {...props} />
  </StripeContainer>
)
